import { PageHeader, PageShell } from '@/components/PageShell';
import { TabNav } from '@/components/TabNav';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { brandDetailsState, brandModelsState, brandSourceState } from '@/state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import {
    brandOwnedDomains,
    brandTrustedDomains,
    planBrandAliasRemoval,
    withBrandDomainAdded,
    withBrandDomainPinned,
    withBrandDomainRemoved,
    withResearchedDomainRemoved,
    withTrustedDomainAdded,
    withTrustedDomainRemoved,
} from '@rock/brands';
import { BrandAliasDao, BrandDao } from '@rock/db-admin';
import type {
    Brand,
    BrandAssetSlot,
    BrandLine,
    BrandfetchCandidate,
    BrandId,
    BrandLogoImage,
    BrandReviewStatus,
    Timestamp,
} from '@rock/types';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Link, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { BrandAliasesTab } from './BrandAliasesTab';
import { BrandDetailsTab, type BrandDetailsUpdate } from './BrandDetailsTab';
import { BrandDomainsTab } from './BrandDomainsTab';
import { domainRefusalMessage } from './brandDomainRows';
import { BrandModelsTab } from './BrandModelsTab';
import { BRAND_REVIEW_STATUS_META } from './brandReviewStatus';
import { BrandReviewStatusControl } from './BrandReviewStatusControl';
import { useCurrentUserId } from '@/db/useCurrentUserId';
import { addBrandAlias, addBrandLine, convertAliasToLine, removeBrandLine } from './brandCatalogActions';
import { BrandLinesTab } from './BrandLinesTab';
import { enrichBrandFromBrandfetch } from './brandfetchApi';
import { MergeBrandDialog } from './MergeBrandDialog';
import { SharedNameplateDialog } from '@/app/shared-nameplates/SharedNameplateDialog';

const log = logger('BrandDetailsPage');
const brandDao = new BrandDao();
const brandAliasDao = new BrandAliasDao();

export const BrandDetailsPage: FC = () => {
    const by = useCurrentUserId();
    const navigate = useNavigate();
    const { brandId: rawBrandId } = useParams<{ brandId: string }>();
    const brandId = useMemo(() => (rawBrandId ? (decodeURIComponent(rawBrandId) as BrandId) : undefined), [rawBrandId]);

    const [brand, brandLoadable] = useLoadableAtomValue(useMemo(() => brandDetailsState(brandId), [brandId]));
    const models = useAsyncAtomValue(
        useMemo(() => brandModelsState(brandId), [brandId]),
        [],
    );
    const liveModels = useMemo(() => models.filter((model) => !model.archivedAt), [models]);
    const source = useAsyncAtomValue(useMemo(() => brandSourceState(brandId), [brandId]));

    const [isEnriching, setIsEnriching] = useState(false);
    const [brandfetchCandidates, setBrandfetchCandidates] = useState<BrandfetchCandidate[]>([]);
    const [isSearchingLogos, setIsSearchingLogos] = useState(false);
    const [logoOptions, setLogoOptions] = useState<BrandLogoImage[]>([]);
    // The merge dialog, with the surviving brand preset when the alias refusal opened it.
    const [merge, setMerge] = useState<{ loser: Brand; winner?: Brand }>();
    const [sharing, setSharing] = useState(false);
    const mergedInto = useAsyncAtomValue(
        useMemo(() => brandDetailsState(brand?.mergedInto), [brand?.mergedInto]),
        undefined,
    );

    const basePath = brandId ? `/products/brands/${encodeURIComponent(brandId)}` : '/products/brands';

    /** A merged brand is a tombstone: everything it had belongs to the winner, so nothing is edited here. */
    function isMerged(): boolean {
        if (!brand?.mergedInto) return false;
        toast.error('This brand was merged; edit the surviving brand instead.');
        return true;
    }

    async function handleSaveDetails(updates: BrandDetailsUpdate) {
        if (!brandId) return;
        if (isMerged()) return;
        try {
            await brandDao.updateBrandDetails(brandId, updates);
            toast.success('Brand updated');
        } catch (err) {
            log.error('Failed to update brand details', { err, brandId });
            toast.error('Unable to update brand');
            throw err;
        }
    }

    async function handleApplyDisplayName(displayName: string) {
        if (!brandId) return;
        if (isMerged()) return;
        try {
            await brandDao.updateBrandDisplayName(brandId, displayName);
            toast.success(`Display name set to “${displayName}”`);
        } catch (err) {
            log.error('Failed to update brand display name', { err, brandId });
            toast.error('Unable to update brand');
            throw err;
        }
    }

    async function handleBrandfetch(domain?: string) {
        if (!brandId) return;
        setIsEnriching(true);
        if (domain) setBrandfetchCandidates([]);
        try {
            const result = await enrichBrandFromBrandfetch({ brandId, ...(domain ? { domain } : {}) });
            if (result.status === 'applied') {
                setBrandfetchCandidates([]);
                toast.success(`Brand data applied from ${result.domain}`);
            } else if (result.status === 'candidates') {
                setBrandfetchCandidates(result.candidates);
            } else {
                setBrandfetchCandidates([]);
                toast.info('Brandfetch has no data for this brand.');
            }
        } catch (err) {
            log.error('Brandfetch enrichment failed', { err, brandId, domain });
            toast.error('Brandfetch lookup failed');
        } finally {
            setIsEnriching(false);
        }
    }

    async function handleSearchLogos(query: string) {
        if (!brandId) return;
        setIsSearchingLogos(true);
        setLogoOptions([]);
        try {
            const fn = httpsCallable<{ brandId: BrandId; query: string }, { options: BrandLogoImage[] }>(
                getFunctions(),
                'searchForBrandLogos',
            );
            const result = await fn({ brandId, query });
            setLogoOptions(result.data.options);
            if (result.data.options.length === 0) {
                toast.info('The web image search found no logo options.');
            }
        } catch (err) {
            log.error('Logo search failed', { err, brandId });
            toast.error('Logo search failed');
        } finally {
            setIsSearchingLogos(false);
        }
    }

    async function handleAssignAsset(slot: BrandAssetSlot, asset: BrandLogoImage) {
        if (!brandId) return;
        if (isMerged()) return;
        try {
            await brandDao.setBrandAsset(brandId, slot, asset);
            // Keep logoOptions visible — the two-column picker assigns light AND dark from one search.
            toast.success('Asset assigned');
        } catch (err) {
            log.error('Failed to assign brand asset', { err, brandId, slot });
            toast.error('Unable to assign asset');
        }
    }

    async function handleClearAsset(slot: BrandAssetSlot) {
        if (!brandId) return;
        if (isMerged()) return;
        try {
            await brandDao.setBrandAsset(brandId, slot, null);
            toast.success('Asset cleared');
        } catch (err) {
            log.error('Failed to clear brand asset', { err, brandId, slot });
            toast.error('Unable to clear asset');
        }
    }

    async function handleAddAlias(aliasName: string) {
        if (!brand) return;
        try {
            const outcome = await addBrandAlias(brand, aliasName, by);
            if (outcome.status === 'added') {
                toast.success(`Alias “${outcome.display}” added`);
            } else if (outcome.status === 'already-canonical') {
                toast.info('That’s already this brand’s canonical name.');
            } else if (outcome.status === 'taken') {
                const other = outcome.brand;
                toast.error(`“${aliasName}” already resolves to ${other.canonicalName}`, {
                    description: 'Merge that brand into this one to take over its names.',
                    action: { label: 'Merge…', onClick: () => setMerge({ loser: other, winner: brand }) },
                });
            } else {
                toast.error('That name can’t be used as an alias', { description: outcome.reason });
            }
        } catch (err) {
            log.error('Failed to add alias', { err, brandId: brand.id, aliasName });
            toast.error('Unable to add alias');
            throw err;
        }
    }

    async function handleAddDomain(domainInput: string) {
        if (!brand) return;
        if (isMerged()) return;
        const next = withBrandDomainAdded(brand, domainInput);
        if (!next) {
            const refusal = domainRefusalMessage(brand, domainInput);
            toast.error(refusal);
            throw new Error(refusal);
        }
        try {
            await brandDao.setBrandDomains(brand.id, next);
            toast.success('Domain added');
        } catch (err) {
            log.error('Failed to add domain', { err, brandId: brand.id, domainInput });
            toast.error('Unable to add domain');
            throw err;
        }
    }

    /** The Summary card's website pin link — see `withBrandDomainPinned` for why an implicit domain needs it. */
    async function handlePinDomain(domain: string) {
        if (!brand) return;
        if (isMerged()) return;
        const next = withBrandDomainPinned(brand, domain);
        if (!next) {
            toast.info('That domain is already in the owned domains list.');
            return;
        }
        try {
            await brandDao.setBrandDomains(brand.id, next);
            toast.success(`“${domain}” added to owned domains`);
        } catch (err) {
            log.error('Failed to pin domain', { err, brandId: brand.id, domain });
            toast.error('Unable to add domain');
        }
    }

    async function handleRemoveDomain(domain: string) {
        if (!brand) return;
        if (isMerged()) return;
        const nextExplicit = withBrandDomainRemoved(brand, domain);
        try {
            if (nextExplicit !== null) {
                await brandDao.setBrandDomains(brand.id, nextExplicit);
            } else {
                const nextResearched = withResearchedDomainRemoved(brand, domain);
                if (nextResearched === null) return;
                await brandDao.setBrandResearchedDomains(brand.id, nextResearched);
            }
            toast.success(`Domain “${domain}” removed`);
        } catch (err) {
            log.error('Failed to remove domain', { err, brandId: brand.id, domain });
            toast.error('Unable to remove domain');
        }
    }

    async function handleAddTrustedDomain(domainInput: string) {
        if (!brand) return;
        if (isMerged()) return;
        const next = withTrustedDomainAdded(brand, domainInput);
        if (!next) {
            const refusal = domainRefusalMessage(brand, domainInput);
            toast.error(refusal);
            throw new Error(refusal);
        }
        try {
            await brandDao.setBrandTrustedDomains(brand.id, next);
            toast.success('Trusted domain added');
        } catch (err) {
            log.error('Failed to add trusted domain', { err, brandId: brand.id, domainInput });
            toast.error('Unable to add domain');
            throw err;
        }
    }

    async function handleRemoveTrustedDomain(domain: string) {
        if (!brand) return;
        if (isMerged()) return;
        const next = withTrustedDomainRemoved(brand, domain);
        if (next === null) return;
        try {
            await brandDao.setBrandTrustedDomains(brand.id, next);
            toast.success(`Trusted domain “${domain}” removed`);
        } catch (err) {
            log.error('Failed to remove trusted domain', { err, brandId: brand.id, domain });
            toast.error('Unable to remove domain');
        }
    }

    async function handleReviewStatusChange(status: BrandReviewStatus) {
        if (!brandId) return;
        try {
            await brandDao.setBrandReviewStatus(brandId, status, new Date().toISOString() as Timestamp);
            toast.success(`Marked ${BRAND_REVIEW_STATUS_META[status].label.toLowerCase()}`);
        } catch (err) {
            log.error('Failed to update brand review status', { err, brandId, status });
            toast.error('Unable to update status');
        }
    }

    async function handleAddLine(displayName: string) {
        if (!brand || isMerged()) return;
        try {
            const outcome = await addBrandLine(brand, { displayName }, by);
            if (outcome.status === 'refused') {
                toast.error('That can’t be a line', { description: outcome.reason });
                throw new Error(outcome.reason);
            }
            toast.success(`Line “${outcome.line.displayName}” added`);
        } catch (err) {
            log.error('Failed to add line', { err, brandId: brand.id, displayName });
            throw err;
        }
    }

    async function handleRemoveLine(line: BrandLine) {
        if (!brand || isMerged()) return;
        try {
            await removeBrandLine(brand, line);
            toast.success(`Line “${line.displayName}” removed`);
        } catch (err) {
            log.error('Failed to remove line', { err, brandId: brand.id, lineId: line.id });
            toast.error('Unable to remove line');
        }
    }

    async function handleAliasToLine(aliasName: string) {
        if (!brand || isMerged()) return;
        try {
            const outcome = await convertAliasToLine(brand, aliasName, by);
            if (outcome.status === 'refused') {
                toast.error('Not a line of this brand', { description: outcome.reason });
                return;
            }
            toast.success(`“${aliasName}” is now the ${outcome.line.displayName} line`);
            navigate(`${basePath}/lines`);
        } catch (err) {
            log.error('Failed to convert alias to line', { err, brandId: brand.id, aliasName });
            toast.error('Unable to convert the alias');
        }
    }

    async function handleRemoveAlias(aliasName: string) {
        if (!brand) return;
        if (brand.mergedInto) {
            // Its alias docs now resolve to the winner; deleting them here would break the winner's names.
            toast.error('This brand was merged; manage its names on the surviving brand.');
            return;
        }
        try {
            // Read fresh: a removal must know every doc the brand owns right now, not a list still loading.
            const plan = planBrandAliasRemoval(brand, aliasName, await brandAliasDao.findAliasesByBrandId(brand.id));
            if (!plan) {
                toast.error('The canonical name can’t be removed.');
                return;
            }
            await brandDao.applyBrandAliasRemoval(brand.id, plan, new Date().toISOString() as Timestamp);
            log.info('Removed brand alias', { brandId: brand.id, aliasName, deleted: plan.deleteKeys.length });
            toast.success(`Alias “${aliasName}” removed`);
        } catch (err) {
            log.error('Failed to remove alias', { err, brandId: brand.id, aliasName });
            toast.error('Unable to remove alias');
        }
    }

    return (
        <PageShell width="medium">
            <PageHeader
                title={brand?.displayName ?? brand?.canonicalName ?? 'Brand details'}
                id={brandId}
                backLabel="Back to brands"
            >
                {brand && !brand.mergedInto ? (
                    <>
                        <Button variant="outline" className="self-start" onClick={() => setSharing(true)}>
                            Shared nameplate…
                        </Button>
                        <Button variant="outline" className="self-start" onClick={() => setMerge({ loser: brand })}>
                            Merge into…
                        </Button>
                    </>
                ) : null}
                {brand ? <BrandReviewStatusControl brand={brand} onChange={handleReviewStatusChange} /> : null}
            </PageHeader>

            {brand?.mergedInto && (
                <Card className="border-amber-300 bg-amber-50">
                    <CardHeader>
                        <CardTitle>Merged into {mergedInto?.canonicalName ?? brand.mergedInto}</CardTitle>
                        <CardDescription>
                            This brand is a tombstone
                            {brand.mergedAt ? ` since ${new Date(brand.mergedAt).toLocaleDateString()}` : ''}: its
                            names, models and recalls now belong to the surviving brand, and nothing resolves here.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-wrap gap-2">
                        <Button asChild>
                            <Link to={`/products/brands/${encodeURIComponent(brand.mergedInto)}`}>
                                Open the surviving brand
                            </Link>
                        </Button>
                        {mergedInto && (
                            <Button variant="outline" onClick={() => setMerge({ loser: brand, winner: mergedInto })}>
                                Re-run merge
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}

            {!brand ? (
                <Card>
                    <CardHeader>
                        <CardTitle>
                            {brandLoadable.state === 'loading' ? 'Loading brand…' : 'Brand not found'}
                        </CardTitle>
                        <CardDescription>
                            {brandLoadable.state === 'loading'
                                ? 'Fetching the brand record.'
                                : 'We could not find a document for this brand. It may have been removed or renamed.'}
                        </CardDescription>
                    </CardHeader>
                </Card>
            ) : (
                <>
                    <TabNav
                        tabs={[
                            { label: 'Details', to: `${basePath}/details` },
                            { label: 'Aliases', to: `${basePath}/aliases`, count: brand.aliases.length },
                            {
                                label: 'Domains',
                                to: `${basePath}/domains`,
                                count: brandOwnedDomains(brand).length + brandTrustedDomains(brand).length,
                            },
                            { label: 'Lines', to: `${basePath}/lines`, count: brand.lines?.length ?? 0 },
                            { label: 'Models', to: `${basePath}/models`, count: liveModels.length },
                        ]}
                    />
                    <Routes>
                        <Route index element={<Navigate replace to="./details" />} />
                        <Route
                            path="details"
                            element={
                                <BrandDetailsTab
                                    brand={brand}
                                    source={source}
                                    isEnriching={isEnriching}
                                    brandfetchCandidates={brandfetchCandidates}
                                    onBrandfetch={handleBrandfetch}
                                    onDismissCandidates={() => setBrandfetchCandidates([])}
                                    onPinDomain={handlePinDomain}
                                    isSearchingLogos={isSearchingLogos}
                                    logoOptions={logoOptions}
                                    onSearchLogos={handleSearchLogos}
                                    onAssignAsset={handleAssignAsset}
                                    onClearAsset={handleClearAsset}
                                    onSave={handleSaveDetails}
                                    onApplyDisplayName={handleApplyDisplayName}
                                />
                            }
                        />
                        <Route
                            path="aliases"
                            element={
                                <BrandAliasesTab
                                    brand={brand}
                                    readOnly={!!brand.mergedInto}
                                    onAddAlias={handleAddAlias}
                                    onRemoveAlias={handleRemoveAlias}
                                    onAliasToLine={handleAliasToLine}
                                />
                            }
                        />
                        <Route
                            path="domains"
                            element={
                                <BrandDomainsTab
                                    brand={brand}
                                    readOnly={!!brand.mergedInto}
                                    onAddDomain={handleAddDomain}
                                    onRemoveDomain={handleRemoveDomain}
                                    onAddTrustedDomain={handleAddTrustedDomain}
                                    onRemoveTrustedDomain={handleRemoveTrustedDomain}
                                />
                            }
                        />
                        <Route
                            path="lines"
                            element={
                                <BrandLinesTab
                                    brand={brand}
                                    readOnly={!!brand.mergedInto}
                                    onAddLine={handleAddLine}
                                    onRemoveLine={handleRemoveLine}
                                />
                            }
                        />
                        <Route
                            path="models"
                            element={
                                <BrandModelsTab models={liveModels} archivedCount={models.length - liveModels.length} />
                            }
                        />
                    </Routes>
                </>
            )}
            {brand && sharing && (
                <SharedNameplateDialog
                    open
                    onOpenChange={(open) => !open && setSharing(false)}
                    initialName={brand.canonicalName}
                />
            )}
            {merge && (
                <MergeBrandDialog
                    open
                    onOpenChange={(open) => !open && setMerge(undefined)}
                    loser={merge.loser}
                    winner={merge.winner}
                />
            )}
        </PageShell>
    );
};
